import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useAppDispatch } from '@/app/store/hooks';
import { clearTokens } from '@/shared/lib/tokenStorage';

import { useShortUrlLoginMutation } from './auth.api';
import { signedOut } from './auth.slice';
import { nextRouteFor } from './linkToken';

/**
 * Exchanges a short link (`/u/:token`) for a session.
 *
 * Same shape as `useLinkLogin`, with one difference that matters: the payload
 * is not in the URL, so the destination cannot be computed until the server
 * answers. Everything else — clearing the previous session first, guarding
 * against StrictMode's double-invoke on a single-use token — is identical, and
 * for the same reasons.
 *
 * A short link always routes through OTP. The legacy portal did the same: the
 * token in a short URL is guessable in a way an encoded payload is not, so the
 * second factor is what stands between a shoulder-surfed link and a session.
 */
export const useShortLinkLogin = (): void => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [shortUrlLogin] = useShortUrlLoginMutation();

  const exchanged = useRef(false);

  useEffect(() => {
    if (token === undefined || exchanged.current) return;
    exchanged.current = true;

    clearTokens();
    dispatch(signedOut());

    const run = async (): Promise<void> => {
      try {
        const { decodedObject } = await shortUrlLogin({ shortUrlToken: token }).unwrap();

        void navigate('/otp', {
          replace: true,
          state: {
            asicMemberId: decodedObject.asicMemberId,
            nextRoute: nextRouteFor(decodedObject, window.screen.width, window.location.search),
            otpExpiry: 0,
          },
        });
      } catch {
        void navigate('/error', { replace: true });
      }
    };

    void run();
  }, [token, dispatch, navigate, shortUrlLogin]);
};
